import React, { useMemo } from 'react';
import {
  View,
  Text,
  StyleSheet,
  TouchableOpacity,
} from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { Platform } from 'react-native';
import { colors, spacing, typography } from '../../theme';
import { useLocale } from '../../contexts/LocaleContext';
import { useRTLStyles } from '../../hooks/useRTLStyles';
import { useThemedStyles } from '../../hooks/useThemedStyles';
import { withAlpha } from '../../theme';

export interface CalendarMonthViewProps {
  currentDate: Date;
  onDateSelect?: (date: Date) => void;
  selectedDate?: Date;
  events?: Array<{ date: Date; count: number }>;
  fullEvents?: Array<any>; // Full event objects with calendar info
  calendarsMap?: Map<string, any>; // Map of calendarId to calendar object
  onEventPress?: (event: any) => void; // Callback when event is clicked
  onPreviousMonth?: () => void;
  onNextMonth?: () => void;
  minDate?: Date;
  maxDate?: Date;
  dayCellSize?: number; // Optional: fixed size for day cells to ensure they fit
}

export const CalendarMonthView: React.FC<CalendarMonthViewProps> = ({
  currentDate,
  onDateSelect,
  selectedDate,
  events = [],
  fullEvents = [],
  calendarsMap,
  onEventPress,
  onPreviousMonth,
  onNextMonth,
  minDate,
  maxDate,
  dayCellSize,
}) => {
  const styles = useThemedStyles(getStyles);
  const { t } = useLocale();
  const rtl = useRTLStyles();
  const DAYS_OF_WEEK = [
    t('calendar.monthView.daySun'),
    t('calendar.monthView.dayMon'),
    t('calendar.monthView.dayTue'),
    t('calendar.monthView.dayWed'),
    t('calendar.monthView.dayThu'),
    t('calendar.monthView.dayFri'),
    t('calendar.monthView.daySat'),
  ];
  // Memoize calendar days calculation to prevent recalculation on re-renders
  const calendarDays = useMemo(() => {
    const year = currentDate.getFullYear();
    const month = currentDate.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const daysInMonth = lastDay.getDate();
    const startingDayOfWeek = firstDay.getDay();

    const today = new Date();
    today.setHours(0, 0, 0, 0);
    
    const selectedDateOnly = selectedDate ? new Date(selectedDate) : null;
    if (selectedDateOnly) {
      selectedDateOnly.setHours(0, 0, 0, 0);
    }

    const days = [];
    const now = today.getTime();

    // Create events map for quick lookup (count-based)
    const eventsMap = new Map<string, number>();
    events.forEach(event => {
      const eventDate = new Date(event.date);
      eventDate.setHours(0, 0, 0, 0);
      const dateKey = eventDate.toISOString().split('T')[0];
      eventsMap.set(dateKey, event.count || 0);
    });

    // Create full events map by date (for displaying actual events)
    const fullEventsMap = new Map<string, any[]>();
    fullEvents.forEach(event => {
      if (event.startDate) {
        const eventDate = new Date(event.startDate);
        eventDate.setHours(0, 0, 0, 0);
        const dateKey = eventDate.toISOString().split('T')[0];
        if (!fullEventsMap.has(dateKey)) {
          fullEventsMap.set(dateKey, []);
        }
        fullEventsMap.get(dateKey)!.push(event);
      }
    });

    // Empty cells for days before the first day of month
    for (let i = 0; i < startingDayOfWeek; i++) {
      days.push({
        day: null,
        isCurrentMonth: false,
        date: null,
      });
    }

    // Days of the month
    for (let day = 1; day <= daysInMonth; day++) {
      const currentDateObj = new Date(year, month, day);
      currentDateObj.setHours(0, 0, 0, 0);
      const currentDateOnly = currentDateObj.getTime();
      const dateKey = currentDateObj.toISOString().split('T')[0];
      
      const isPast = minDate 
        ? currentDateOnly < minDate.getTime()
        : currentDateOnly < now - 86400000; // 24 hours in ms
      
      const isFuture = maxDate 
        ? currentDateOnly > maxDate.getTime()
        : false;
      
      const isSelected = selectedDateOnly && currentDateOnly === selectedDateOnly.getTime();
      const isToday = currentDateOnly === now;
      const eventCount = eventsMap.get(dateKey) || 0;
      const dayEvents = fullEventsMap.get(dateKey) || [];

      days.push({
        day,
        isCurrentMonth: true,
        date: currentDateObj,
        isPast,
        isFuture,
        isSelected,
        isToday,
        eventCount,
        dayEvents, // Include full events for this day
      });
    }

    return days;
  }, [currentDate, selectedDate, events, fullEvents, minDate, maxDate]);

  const monthYearText = currentDate.toLocaleDateString('en-US', { 
    month: 'long', 
    year: 'numeric' 
  });

  const handleDatePress = (date: Date | null) => {
    if (date && onDateSelect) {
      onDateSelect(date);
    }
  };

  return (
    <View style={styles.container}>
      {/* Calendar Header */}
      <View style={[styles.header, { flexDirection: rtl.rowDirection }]}>
        <TouchableOpacity
          onPress={onPreviousMonth}
          style={styles.navButton}
          activeOpacity={0.7}
        >
          <Ionicons name={rtl.mirrorIcon('chevron-back') as any} size={24} color={colors.primary} />
        </TouchableOpacity>
        <Text style={styles.monthYear}>{monthYearText}</Text>
        <TouchableOpacity
          onPress={onNextMonth}
          style={styles.navButton}
          activeOpacity={0.7}
        >
          <Ionicons name={rtl.mirrorIcon('chevron-forward') as any} size={24} color={colors.primary} />
        </TouchableOpacity>
      </View>

      {/* Day Headers - Fixed 7 columns */}
      <View style={styles.dayHeaders}>
        {DAYS_OF_WEEK.map((day) => (
          <View key={day} style={styles.dayHeader}>
            <Text style={styles.dayHeaderText}>{day}</Text>
          </View>
        ))}
      </View>

      {/* Calendar Days Grid - Fixed 7 columns */}
      <View style={styles.calendarGrid}>
        {calendarDays.map((dayData, index) => {
          if (!dayData.day) {
            // Empty cell for days before the first day of month
            return (
              <View 
                key={`empty-${index}`} 
                style={[
                  styles.dayCell,
                  dayCellSize && {
                    // Keep width as percentage (14.28%) to ensure 7 days per row
                    // Only apply dayCellSize to height to fill available space
                    height: dayCellSize,
                    minHeight: dayCellSize,
                    maxHeight: dayCellSize,
                  },
                ]} 
              />
            );
          }

          const { day, date, isPast, isFuture, isSelected, isToday, eventCount, dayEvents = [] } = dayData;
          const isDisabled = isPast || isFuture;
          const hasEvents = dayEvents.length > 0;

          return (
            <TouchableOpacity
              key={`day-${day}`}
              style={[
                styles.dayCell,
                isSelected && styles.dayCellSelected,
                isToday && !isSelected && styles.dayCellToday,
                !hasEvents && styles.dayCellNoEvents, // Center when no events
                dayCellSize && {
                  // Keep width as percentage (14.28%) to ensure 7 days per row
                  // Only apply dayCellSize to height to fill available space
                  height: dayCellSize,
                  minHeight: dayCellSize,
                  maxHeight: dayCellSize,
                },
              ]}
              onPress={() => handleDatePress(date)}
              disabled={isDisabled}
              activeOpacity={0.7}
            >
              <View style={[styles.dayCellHeader, !hasEvents && styles.dayCellHeaderCentered]}>
                <Text
                  style={[
                    styles.dayText,
                    isSelected && styles.dayTextSelected,
                    isToday && !isSelected && styles.dayTextToday,
                    isDisabled && styles.dayTextDisabled,
                  ]}
                >
                  {day}
                </Text>
              </View>
              
              {/* Display event cards */}
              {hasEvents && (
                <View style={styles.dayEventsContainer}>
                  {dayEvents.slice(0, 2).map((event, idx) => {
                    const calendar = event.calendarId && calendarsMap ? calendarsMap.get(event.calendarId) : null;
                    const calendarColor = calendar?.color || colors.primary;
                    
                    return (
                      <TouchableOpacity
                        key={event._id || idx}
                        style={[styles.eventCardInCell, { borderLeftColor: calendarColor, flexDirection: rtl.rowDirection }]}
                        onPress={(e) => {
                          e.stopPropagation();
                          if (onEventPress) {
                            onEventPress(event);
                          }
                        }}
                        activeOpacity={0.7}
                      >
                        <View style={[styles.eventCardTag, { backgroundColor: calendarColor + '20', flexDirection: rtl.rowDirection }]}>
                          <View style={[styles.eventCardDot, { backgroundColor: calendarColor }]} />
                        </View>
                        <Text style={styles.eventCardTitle} numberOfLines={1} ellipsizeMode="tail">
                          {event.title || t('calendar.monthView.untitledEvent')}
                        </Text>
                      </TouchableOpacity>
                    );
                  })}
                  {dayEvents.length > 2 && (
                    <TouchableOpacity
                      style={styles.moreEventsButton}
                      onPress={(e) => {
                        e.stopPropagation();
                        if (onEventPress && dayEvents.length > 0) {
                          onEventPress(dayEvents[0]);
                        }
                      }}
                      activeOpacity={0.7}
                    >
                      <Text style={styles.moreEventsText}>{t('calendar.monthView.moreEvents', { count: dayEvents.length - 2 })}</Text>
                    </TouchableOpacity>
                  )}
                </View>
              )}
            </TouchableOpacity>
          );
        })}
      </View>
    </View>
  );
};

const getStyles = () => StyleSheet.create({
  container: {
    backgroundColor: colors.background.surface,
    borderRadius: spacing.borderRadius.lg,
    padding: spacing.md,
    marginBottom: spacing.md,
    width: '100%',
    ...Platform.select({
      web: {
        boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)',
      },
      default: {
        elevation: 2,
      },
    }),
  },
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: spacing.md,
    paddingHorizontal: spacing.xs,
  },
  navButton: {
    padding: spacing.xs,
    borderRadius: spacing.borderRadius.sm,
  },
  monthYear: {
    ...typography.h3,
    color: colors.textDark,
    fontFamily: typography.fontFamily.semibold,
    flex: 1,
    textAlign: 'center',
  },
  dayHeaders: {
    flexDirection: 'row',
    marginBottom: spacing.xs,
    // Fixed 7 columns - each taking 14.28% (1/7)
    width: '100%',
  },
  dayHeader: {
    width: '14.28%', // Fixed: 100% / 7 = 14.28%
    alignItems: 'center',
    justifyContent: 'center',
    paddingVertical: spacing.xs,
  },
  dayHeaderText: {
    ...typography.caption,
    color: colors.text.secondary,
    fontFamily: typography.fontFamily.medium,
    fontSize: typography.fontSize.sm,
  },
  calendarGrid: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    width: '100%',
    // Ensure consistent layout - 7 columns
  },
  dayCell: {
    // Default: 14.28% width (100% / 7)
    // Will be overridden by dayCellSize if provided
    width: '14.28%',
    alignItems: 'center',
    justifyContent: 'flex-start',
    padding: spacing.xs,
    position: 'relative',
    minHeight: 30, // Minimum size for readability
    minWidth: 30, // Minimum width
    overflow: 'hidden', // Prevent content from overflowing
  },
  dayCellSelected: {
    backgroundColor: colors.primary,
    borderRadius: spacing.borderRadius.md,
  },
  dayCellToday: {
    backgroundColor: withAlpha(colors.primary, 0.13), // 20% opacity
    borderRadius: spacing.borderRadius.md,
  },
  dayText: {
    ...typography.body,
    color: colors.text.primary,
    fontSize: typography.fontSize.base,
  },
  dayTextSelected: {
    color: colors.white,
    fontFamily: typography.fontFamily.semibold,
  },
  dayTextToday: {
    color: colors.primary,
    fontFamily: typography.fontFamily.semibold,
  },
  dayTextDisabled: {
    color: colors.text.secondary,
    opacity: 0.3,
  },
  eventIndicator: {
    position: 'absolute',
    bottom: 2,
    right: 2,
    backgroundColor: colors.primary,
    borderRadius: 8,
    minWidth: 16,
    height: 16,
    paddingHorizontal: 4,
    alignItems: 'center',
    justifyContent: 'center',
  },
  eventIndicatorText: {
    ...typography.caption,
    color: colors.white,
    fontSize: 9,
    fontFamily: typography.fontFamily.semibold,
  },
  dayCellHeader: {
    width: '100%',
    alignItems: 'flex-start',
    paddingTop: spacing.xs,
    paddingHorizontal: spacing.xs / 2,
    minHeight: 20, // Reserve space for day number
  },
  dayCellHeaderCentered: {
    alignItems: 'center',
    justifyContent: 'center',
    flex: 1,
  },
  dayCellNoEvents: {
    justifyContent: 'center',
  },
  dayEventsContainer: {
    width: '100%',
    paddingHorizontal: spacing.xs / 2,
    paddingBottom: spacing.xs,
    gap: 2,
    justifyContent: 'flex-start',
    maxHeight: '100%',
    overflow: 'hidden', // Prevent overflow
  },
  eventCardInCell: {
    width: '100%',
    backgroundColor: colors.background.secondary,
    borderRadius: 4,
    borderLeftWidth: 3,
    padding: 4,
    flexDirection: 'row',
    alignItems: 'center',
    gap: 4,
    minHeight: 20,
    maxHeight: 24, // Limit height to prevent overflow
    overflow: 'hidden',
  },
  eventCardTag: {
    flexDirection: 'row',
    alignItems: 'center',
    borderRadius: 2,
    paddingHorizontal: 2,
    paddingVertical: 1,
  },
  eventCardDot: {
    width: 6,
    height: 6,
    borderRadius: 3,
  },
  eventCardTitle: {
    fontSize: 9,
    fontFamily: typography.fontFamily.medium,
    color: colors.text.primary,
    flex: 1,
  },
  moreEventsButton: {
    width: '100%',
    paddingVertical: 2,
    alignItems: 'center',
  },
  moreEventsText: {
    fontSize: 9,
    fontFamily: typography.fontFamily.medium,
    color: colors.primary,
  },
});

